import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { QualityWeek } from '../../core/api.types';

export interface QualityWeekResponse {
  feedbackAvgScore: number;
  feedbackParticipation: number;
  complaintRate: number;
  equityIndex: number;
  isoCompliance: number;
  feedbackCount?: number;
  feedbackLt10?: number;
  feedback10_20?: number;
  feedbackGt20?: number;
  participationHistory: number[];
  complaintHistory: number[];
  waitingPerceptionHistory: {
    courte: number[];
    moyenne: number[];
    longue: number[];
  };
}

export interface QualityMonthResponse {
  feedbackAvgScore: number | null;
  feedbackParticipation: number;
  feedbackCount?: number;
  feedbackLt10?: number;
  feedback10_20?: number;
  feedbackGt20?: number;
  perceived_wait_avg?: number | null;
  complaintRate: number;
  equityIndex: number;
  isoCompliance: number;
  participationHistory: number[];
  complaintHistory: number[];
  waitingPerceptionHistory: {
    courte: number[];
    moyenne: number[];
    longue: number[];
  };
}
import { API_BASE_URL } from '../../core/api-base-url.token';

export interface WeekAvailability {
  isoWeek: number;
  hasData: boolean;
}

export interface MonthAvailability {
  month: number;
  hasData: boolean;
}

export interface ParticipationWeekResponse {
  total_feedback: number;
  total_qr: number;
  overall_participation: number;
  daily_participation: number[];
}

export interface ParticipationMonthResponse {
  current_feedback: number;
  current_qr: number;
  current_participation: number;
  monthly_history: number[];
}

@Injectable({ providedIn: 'root' })
export class QualityService {
  private readonly http = inject(HttpClient);
  /** Base d’URL dynamique (local/dev/prod), normalisée sans / final */
  private readonly baseRoot = inject(API_BASE_URL).replace(/\/+$/, '');
  private readonly base = `${this.baseRoot}/api/v1/quality`;

  /**
   * Récupère l’agrégat qualité d’une semaine (compat legacy).
   * GET /api/v1/quality/weeks?establishment_id=...&year=YYYY&isoWeek=WW
   */
  getWeek(establishmentId: string, isoYear: number, isoWeek: number): Observable<QualityWeek> {
    const params = new HttpParams()
      .set('establishment_id', establishmentId)
      .set('year', String(isoYear))
      .set('isoWeek', String(isoWeek));
    return this.http.get<QualityWeek>(`${this.base}/weeks`, { params });
  }

  /**
   * Exporte les actions manuelles (CSV) pour la semaine sélectionnée.
   * GET /api/v1/quality/weeks/{establishmentId}/exports/manual-actions?isoYear=YYYY&isoWeek=WW
   */
  exportManualActions(establishmentId: string, isoYear: number, isoWeek: number): Observable<Blob> {
    const url = `${this.base}/weeks/${encodeURIComponent(establishmentId)}/exports/manual-actions`;
    const params = new HttpParams()
      .set('isoYear', String(isoYear))
      .set('isoWeek', String(isoWeek));
    return this.http.get(url, { params, responseType: 'blob' as const });
  }

  /**
   * Exporte les retours post-visite (CSV) pour la semaine sélectionnée.
   * GET /api/v1/quality/weeks/{establishmentId}/exports/feedback?isoYear=YYYY&isoWeek=WW
   */
  exportFeedback(establishmentId: string, isoYear: number, isoWeek: number): Observable<Blob> {
    const url = `${this.base}/weeks/${encodeURIComponent(establishmentId)}/exports/feedback`;
    const params = new HttpParams()
      .set('isoYear', String(isoYear))
      .set('isoWeek', String(isoWeek));
    return this.http.get(url, { params, responseType: 'blob' as const });
  }

  /**
   * PDF mensuel IWEA (fiche RAQ) – backend:
   * GET /api/v1/quality/reports/raq-monthly/{establishmentId}/{year}/{month}/pdf?refresh=...
   *
   * On observe la réponse complète pour récupérer le Content-Disposition (nom de fichier).
   */
  downloadMonthlyRaqPdf(
    establishmentId: string,
    year: number,
    month: number,
    refresh = true
  ): Observable<HttpResponse<Blob>> {
    const url = `${this.baseRoot}/api/v1/quality/reports/raq-monthly/${encodeURIComponent(establishmentId)}/${year}/${month}/pdf`;
    const params = new HttpParams().set('refresh', String(refresh));
    return this.http.get(url, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  /**
   * Récupère les données qualité étendues pour une semaine
   * GET /api/v1/quality/weeks/{establishmentId}?isoYear=YYYY&isoWeek=WW
   */
  getWeeklyQuality(establishmentId: string, year: number, week: number): Observable<QualityWeekResponse> {
    return this.http.get<QualityWeekResponse>(
      `${this.base}/weeks/${encodeURIComponent(establishmentId)}?isoYear=${year}&isoWeek=${week}`
    );
  }

  /**
   * Récupère les données qualité pour un mois
   * GET /api/v1/quality/months/{establishmentId}?year=YYYY&month=MM
   */
  getMonthlyQuality(establishmentId: string, year: number, month: number): Observable<QualityMonthResponse> {
    return this.http.get<QualityMonthResponse>(
      `${this.base}/months/${encodeURIComponent(establishmentId)}?year=${year}&month=${month}`
    );
  }

  /**
   * Récupère la satisfaction hebdomadaire
   * GET /api/quality/satisfaction/weekly/{establishmentId}?year={year}&week={week}
   */
  getWeeklySatisfaction(establishmentId: string, year: number, week: number): Observable<{avg_satisfaction: number, satisfaction_count: number}> {
    return this.http.get<{avg_satisfaction: number, satisfaction_count: number}>(
      `${this.baseRoot}/api/quality/satisfaction/weekly/${encodeURIComponent(establishmentId)}?year=${year}&week=${week}`
    );
  }

  /**
   * Récupère la satisfaction mensuelle
   * GET /api/quality/satisfaction/monthly/{establishmentId}?year={year}&month={month}
   */
  getMonthlySatisfaction(establishmentId: string, year: number, month: number): Observable<{avg_satisfaction: number, satisfaction_count: number}> {
    return this.http.get<{avg_satisfaction: number, satisfaction_count: number}>(
      `${this.baseRoot}/api/quality/satisfaction/monthly/${encodeURIComponent(establishmentId)}?year=${year}&month=${month}`
    );
  }

  /**
   * Récupère la participation hebdomadaire
   * GET /api/v1/quality/participation/weekly/{establishmentId}?isoYear={year}&isoWeek={week}
   */
  getWeeklyParticipation(establishmentId: string, isoYear: number, isoWeek: number): Observable<ParticipationWeekResponse> {
    return this.http.get<ParticipationWeekResponse>(
      `${this.base}/participation/weekly/${encodeURIComponent(establishmentId)}?isoYear=${isoYear}&isoWeek=${isoWeek}`
    );
  }

  /**
   * Récupère la participation mensuelle
   * GET /api/v1/quality/participation/monthly/{establishmentId}?year={year}&month={month}
   */
  getMonthlyParticipation(establishmentId: string, year: number, month: number): Observable<ParticipationMonthResponse> {
    return this.http.get<ParticipationMonthResponse>(
      `${this.base}/participation/monthly/${encodeURIComponent(establishmentId)}?year=${year}&month=${month}`
    );
  }

  // ===== NOUVEAUX ENDPOINTS =====

  /**
   * Données semaine
   * GET /api/v1/quality/establishments/{id}/weeks/{year}/{week}
   */
  getEstablishmentWeek(establishmentId: string, year: number, week: number): Observable<any> {
    return this.http.get<any>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/weeks/${year}/${week}`
    );
  }

  /**
   * Données mois
   * GET /api/v1/quality/establishments/{id}/months/{year}/{month}
   */
  getEstablishmentMonth(establishmentId: string, year: number, month: number): Observable<any> {
    return this.http.get<any>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/months/${year}/${month}`
    );
  }

  /**
   * Participation semaine
   * GET /api/v1/quality/establishments/{id}/participation/weeks/{year}/{week}
   */
  getEstablishmentWeekParticipation(establishmentId: string, year: number, week: number): Observable<ParticipationWeekResponse> {
    return this.http.get<ParticipationWeekResponse>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/participation/weeks/${year}/${week}`
    );
  }

  /**
   * Participation mois
   * GET /api/v1/quality/establishments/{id}/participation/months/{year}/{month}
   */
  getEstablishmentMonthParticipation(establishmentId: string, year: number, month: number): Observable<ParticipationMonthResponse> {
    return this.http.get<ParticipationMonthResponse>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/participation/months/${year}/${month}`
    );
  }

  /**
   * Disponibilité semaines
   * GET /api/v1/quality/establishments/{id}/weeks-availability/{year}
   */
  getEstablishmentWeeksAvailability(establishmentId: string, year: number): Observable<WeekAvailability[]> {
    return this.http.get<WeekAvailability[]>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/weeks-availability/${year}`
    );
  }

  /**
   * Disponibilité mois
   * GET /api/v1/quality/establishments/{id}/months-availability/{year}
   */
  getEstablishmentMonthsAvailability(establishmentId: string, year: number): Observable<MonthAvailability[]> {
    return this.http.get<MonthAvailability[]>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/months-availability/${year}`
    );
  }

  /**
   * Analytics pour graphiques (par établissement)
   * GET /api/v1/quality/analytics/{establishmentId}?period=week&date=YYYY-MM-DD
   */
  getAnalytics(establishmentId: string, period: 'week' | 'month', date: string): Observable<any> {
    return this.http.get<any>(
      `${this.base}/analytics/${encodeURIComponent(establishmentId)}?period=${period}&date=${date}`
    );
  }

  /**
   * Analytics agrégés multi-sites (toute l'organisation de l'utilisateur connecté)
   * GET /api/v1/quality/org/analytics?period=week&date=YYYY-MM-DD
   */
  getOrgAnalytics(period: 'week' | 'month', date: string): Observable<any> {
    return this.http.get<any>(`${this.base}/org/analytics?period=${period}&date=${date}`);
  }

  getOrgMonthsAvailability(year: number): Observable<MonthAvailability[]> {
    return this.http.get<MonthAvailability[]>(`${this.base}/org/months-availability/${year}`);
  }

  getOrgWeeksAvailability(year: number): Observable<WeekAvailability[]> {
    return this.http.get<WeekAvailability[]>(`${this.base}/org/weeks-availability/${year}`);
  }

  /**
   * Actualise les données d'un établissement
   * POST /api/v1/quality/establishments/{establishmentId}/refresh-data
   */
  refreshEstablishmentData(establishmentId: string): Observable<any> {
    return this.http.post<any>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/refresh-data`,
      {}
    );
  }

  /**
   * Génère un rapport mensuel RAQ
   * POST /api/v1/quality/reports/raq-monthly/{establishmentId}/{year}/{month}/generate
   */
  generateMonthlyReport(establishmentId: string, year: number, month: number): Observable<{success: boolean, message: string, establishmentId: string, period: string}> {
    return this.http.post<{success: boolean, message: string, establishmentId: string, period: string}>(
      `${this.baseRoot}/api/v1/quality/reports/raq-monthly/${encodeURIComponent(establishmentId)}/${year}/${month}/generate`,
      {}
    );
  }

}
